import { useCallback, useEffect, useState } from 'react'

type Theme = 'light' | 'dark' | 'paper'
const STORAGE_KEY = 'radiri-theme'
const THEME_CHANGE_EVENT = 'radiri:theme-change'

function getInitialTheme(): Theme {
  const saved = localStorage.getItem(STORAGE_KEY)
  if (saved === 'dark' || saved === 'light' || saved === 'paper') return saved
  if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'
  return 'light'
}

/**
 * Reads/writes the active theme to localStorage and html[data-theme].
 * Also listens for 'radiri:theme-change' custom events so that the
 * AuthContext can push the server-stored preference after login.
 */
export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(getInitialTheme)

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  // Listen for external theme changes (e.g. pushed by AuthContext after login sync).
  useEffect(() => {
    function handler(e: Event) {
      const incoming = (e as CustomEvent<{ theme: string }>).detail.theme
      if (incoming === 'light' || incoming === 'dark' || incoming === 'paper') {
        setTheme(incoming)
      }
    }
    window.addEventListener(THEME_CHANGE_EVENT, handler)
    return () => window.removeEventListener(THEME_CHANGE_EVENT, handler)
  }, [])

  const applyTheme = useCallback((t: Theme) => {
    setTheme(t)
  }, [])

  return [theme, applyTheme]
}
